import React, { useState, useMemo } from "react";
import {
  LineChart,
  BarChart,
  PieChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import {
  PieChart as PieChartIcon,
  BarChart2,
  TrendingUp,
  Calendar,
  Users,
  DollarSign,
  Percent,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { WorkOpportunity } from "../../../../store/services/opportunityService";
import MiniStatCard from "../../../ui/cards/MiniStatCard";

// Typer för chart data
interface MonthlyDataItem {
  name: string;
  count: number;
  value: number;
  avgProbability: number;
  probabilities: number[];
  key: string;
}

interface ProbabilityDistributionItem {
  name: string;
  value: number;
}

interface AssigneeDataItem {
  name: string;
  count: number;
  value: number;
}

interface StatsData {
  total: number;
  avgValue: number;
  avgProb: number;
  count: number;
}

interface OpportunityDashboardProps {
  opportunities: WorkOpportunity[];
  isLoading?: boolean;
}

// Helper functions to process data for charts
const prepareMonthlyData = (
  opportunities: WorkOpportunity[]
): MonthlyDataItem[] => {
  // Group by month
  const monthData: Record<string, Omit<MonthlyDataItem, "key">> = {};

  opportunities.forEach((opp) => {
    if (opp.OpportunityCloseDate) {
      const date = parseISO(opp.OpportunityCloseDate);
      const monthKey = `${date.getFullYear()}-${date
        .getMonth()
        .toString()
        .padStart(2, "0")}`;
      const monthName = format(date, "MMM yyyy", { locale: sv });

      if (!monthData[monthKey]) {
        monthData[monthKey] = {
          name: monthName,
          count: 0,
          value: 0,
          avgProbability: 0,
          probabilities: [],
        };
      }

      monthData[monthKey].count += 1;
      monthData[monthKey].value += opp.OpportunityTotalAmount || 0;

      if (opp.OpportunityProbability) {
        monthData[monthKey].probabilities.push(opp.OpportunityProbability);
      }
    }
  });

  // Calculate average probability and sort by date
  const result = Object.keys(monthData).map((key) => {
    const item = monthData[key];
    const totalProb = item.probabilities.reduce((sum, prob) => sum + prob, 0);
    item.avgProbability = item.probabilities.length
      ? totalProb / item.probabilities.length
      : 0;
    return {
      ...item,
      key,
    };
  });

  // Sort by date (using the key which is YYYY-MM)
  return result.sort((a, b) => a.key.localeCompare(b.key));
};

const prepareProbabilityDistribution = (
  opportunities: WorkOpportunity[]
): ProbabilityDistributionItem[] => {
  const probBuckets: Record<string, number> = {
    "0-20%": 0,
    "21-40%": 0,
    "41-60%": 0,
    "61-80%": 0,
    "81-100%": 0,
  };

  opportunities.forEach((opp) => {
    const prob = opp.OpportunityProbability || 0;

    if (prob <= 20) probBuckets["0-20%"] += 1;
    else if (prob <= 40) probBuckets["21-40%"] += 1;
    else if (prob <= 60) probBuckets["41-60%"] += 1;
    else if (prob <= 80) probBuckets["61-80%"] += 1;
    else probBuckets["81-100%"] += 1;
  });

  return Object.entries(probBuckets).map(([name, value]) => ({ name, value }));
};

const prepareAssigneeData = (
  opportunities: WorkOpportunity[]
): AssigneeDataItem[] => {
  const assigneeData: Record<string, AssigneeDataItem> = {};

  opportunities.forEach((opp) => {
    if (opp.AssignedToName) {
      if (!assigneeData[opp.AssignedToName]) {
        assigneeData[opp.AssignedToName] = {
          name: opp.AssignedToName,
          count: 0,
          value: 0,
        };
      }

      assigneeData[opp.AssignedToName].count += 1;
      assigneeData[opp.AssignedToName].value += opp.OpportunityTotalAmount || 0;
    }
  });

  return Object.values(assigneeData)
    .sort((a, b) => b.value - a.value)
    .slice(0, 5); // Top 5
};

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

const OpportunityDashboard: React.FC<OpportunityDashboardProps> = ({
  opportunities = [],
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  // Memoize processed data to avoid recalculation on re-renders
  const monthlyData = useMemo<MonthlyDataItem[]>(
    () => prepareMonthlyData(opportunities),
    [opportunities]
  );

  const probabilityDistribution = useMemo<ProbabilityDistributionItem[]>(
    () => prepareProbabilityDistribution(opportunities),
    [opportunities]
  );

  const assigneeData = useMemo<AssigneeDataItem[]>(
    () => prepareAssigneeData(opportunities),
    [opportunities]
  );

  // Calculate summary statistics
  const stats = useMemo<StatsData>(() => {
    if (!opportunities.length)
      return { total: 0, avgValue: 0, avgProb: 0, count: 0 };

    const totalValue = opportunities.reduce(
      (sum, opp) => sum + (opp.OpportunityTotalAmount || 0),
      0
    );
    const validProbabilityCount = opportunities.filter(
      (opp) => opp.OpportunityProbability != null
    ).length;
    const totalProbability = opportunities.reduce(
      (sum, opp) => sum + (opp.OpportunityProbability || 0),
      0
    );

    return {
      total: totalValue,
      avgValue: totalValue / opportunities.length,
      avgProb: validProbabilityCount
        ? totalProbability / validProbabilityCount
        : 0,
      count: opportunities.length,
    };
  }, [opportunities]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setActiveTab(newValue);
  };

  // Formattera valuta för SEK
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Formattera kompakt valuta (för axlar)
  const formatCompactCurrency = (amount: number): string => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      notation: "compact",
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Hitta mest förekommande sannolikhetsintervall
  const mostCommonProbabilityRange = probabilityDistribution.length
    ? probabilityDistribution.sort((a, b) => b.value - a.value)[0]?.name
    : "N/A";

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MiniStatCard
            title="Totalt värde"
            value={formatCurrency(stats.total)}
            icon={<DollarSign />}
            color="#4caf50"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MiniStatCard
            title="Antal Affärsmöjligheter"
            value={stats.count.toString()}
            icon={<BarChart2 />}
            color="#ff9800"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MiniStatCard
            title="Genomsnittlig sannolikhet"
            value={`${Math.round(stats.avgProb)}%`}
            icon={<Percent />}
            color="#2196f3"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MiniStatCard
            title="Genomsnittligt värde"
            value={formatCurrency(stats.avgValue)}
            icon={<TrendingUp />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Tabs for different charts */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab
              icon={<Calendar size={16} />}
              iconPosition="start"
              label="Tidslinje"
            />
            <Tab
              icon={<PieChartIcon size={16} />}
              iconPosition="start"
              label="Sannolikhet"
            />
            <Tab
              icon={<Users size={16} />}
              iconPosition="start"
              label="Säljare"
            />
          </Tabs>
        </Box>
        <CardContent>
          {/* Timeline Charts */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Värde per månad
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={monthlyData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                    />
                    <Legend />
                    <Bar name="Värde" dataKey="value" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Antal Affärsmöjligheter över tid
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={monthlyData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      name="Antal"
                      type="monotone"
                      dataKey="count"
                      stroke="#82ca9d"
                    />
                    <Line
                      name="Genomsnittlig sannolikhet (%)"
                      type="monotone"
                      dataKey="avgProbability"
                      stroke="#ff7300"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          )}

          {/* Probability Distribution */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Fördelning av sannolikhet
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={probabilityDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} (${(percent * 100).toFixed(0)}%)`
                      }
                    >
                      {probabilityDistribution.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Sannolikhetsanalys
                </Typography>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body1" paragraph>
                    Diagrammet visar fördelningen av Affärsmöjligheter baserat på
                    deras vinstchans (sannolikhet).
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Majoriteten av era Affärsmöjligheter ligger i intervallet{" "}
                    {mostCommonProbabilityRange}.
                  </Typography>
                  <Typography variant="body1">
                    Genomsnittlig sannolikhet för alla möjligheter:{" "}
                    <strong>{Math.round(stats.avgProb)}%</strong>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Sales Rep Performance */}
          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Topp 5 säljare efter värde
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={assigneeData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickFormatter={(value: number) =>
                        formatCompactCurrency(value)
                      }
                    />
                    <YAxis dataKey="name" type="category" width={150} />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                    />
                    <Legend />
                    <Bar name="Totalt värde" dataKey="value" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Antal Affärsmöjligheter per säljare
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={assigneeData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} />
                    <Tooltip />
                    <Legend />
                    <Bar name="Antal" dataKey="count" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OpportunityDashboard;
